import React, { useMemo, useState } from "react";
import {
  Camera,
  Car,
  CheckCircle2,
  FileText,
  Info,
  Landmark,
  Map,
  XCircle,
} from "lucide-react";
import { RichParagraphBlocks } from "./OrganizedTourPublicOfferRichBlocks";
import { buildOrganizedTourOfferTabModel, safeParseOrganizedTourItinerary } from "./organizedTourPublicOfferTabsModel";
import OrganizedTourPublicOfferNotesLegal from "./OrganizedTourPublicOfferNotesLegal";
import OrganizedTourPublicOfferFlightsBlock from "./OrganizedTourPublicOfferFlightsBlock";
import OrganizedTourPublicOfferRoomsBlock from "./OrganizedTourPublicOfferRoomsBlock";
import { extractOrganizedTourFlightLegs } from "../../../utils/organizedTourProfileFlights";

const TAB_META = [
  { id: "describe", label: "תיאור הטיול", Icon: FileText, tone: "describe" },
  { id: "itinerary", label: "מסלול הטיול", Icon: Map, tone: "itinerary" },
  { id: "included", label: "כולל", Icon: CheckCircle2, tone: "included" },
  { id: "excluded", label: "לא כולל", Icon: XCircle, tone: "excluded" },
  { id: "notes", label: "הערות חשובות", Icon: Info, tone: "notes" },
];

/** מסלול הימים מוצג בטאב התיאור (כמו במוקאפ), ולא בטאב נפרד — מונע כפילות */
const EMBED_ITINERARY_IN_DESCRIBE_TAB = true;

/** אייקוני משך ליום (רכב / מצלמה / נקודת צל׳) — כמו במוקאפ */
const DAY_CARD_ICONS = [Car, Camera, Landmark];

function dayBadgeLabel(item, index) {
  const title = String(item.title || "");
  const m = title.match(/^יום\s*(\d+)/);
  if (m) return `יום ${m[1]}`;
  const m2 = title.match(/^יום\s*מס[׳']?\s*(\d+)/i);
  if (m2) return `יום ${m2[1]}`;
  const m3 = title.match(/^Day\s*(\d+)/i);
  if (m3) return `יום ${m3[1]}`;
  if (/^היום\s+ה(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)/iu.test(title)) {
    return `יום ${index + 1}`;
  }
  return `יום ${index + 1}`;
}

/** פירוק לתג מודגש: «יום» + מספר בולט */
function dayBadgeParts(item, index) {
  const full = dayBadgeLabel(item, index).trim();
  let m = full.match(/^יום\s+(\d{1,2})$/);
  if (m) return { kind: "split", prefix: "יום", num: m[1], full };
  m = full.match(/^יום\s*מס[׳']?\s*(\d{1,2})$/i);
  if (m) return { kind: "split", prefix: "יום מס׳", num: m[1], full };
  m = full.match(/^Day\s*(\d{1,2})$/i);
  if (m) return { kind: "split", prefix: "Day", num: m[1], full };
  return { kind: "plain", full };
}

function dayHeadline(item) {
  if (item.subtitle && String(item.subtitle).trim()) return String(item.subtitle).trim();
  const t = String(item.title || "");
  return t
    .replace(/^היום\s+ה(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\s*/iu, "")
    .replace(/^יום\s*מס[׳']?\s*\d+\s*/iu, "")
    .replace(/^יום\s*\d+\s*/i, "")
    .replace(/^Day\s*\d+\s*/i, "")
    .trim() || t;
}

function ItineraryTimelineItem({ item, index }) {
  const listClassName = "organized-tour-public-offer__itinerary-prose-list";

  if (item.type === "day") {
    const k = `day-${index}-${item.title}`;
    const SceneIcon = DAY_CARD_ICONS[index % DAY_CARD_ICONS.length];
    const badge = dayBadgeParts(item, index);
    return (
      <article key={k} className="organized-tour-public-offer__day-card organized-tour-public-offer__day-card--brochure">
        <div className="organized-tour-public-offer__day-card-inner">
          <div className="organized-tour-public-offer__day-card-main">
            <div className="organized-tour-public-offer__day-card-header-row">
              <span className="organized-tour-public-offer__day-badge">
                {badge.kind === "split" ? (
                  <>
                    <span className="organized-tour-public-offer__day-badge-prefix">{badge.prefix}</span>
                    <span className="organized-tour-public-offer__day-badge-num">{badge.num}</span>
                  </>
                ) : (
                  badge.full
                )}
              </span>
              <h3 className="organized-tour-public-offer__day-title">{dayHeadline(item)}</h3>
            </div>
            <div className="organized-tour-public-offer__day-body">
              <RichParagraphBlocks
                paragraphs={item.paragraphs}
                paragraphClassName="organized-tour-public-offer__day-text"
                listClassName={listClassName}
              />
            </div>
          </div>
          <div className="organized-tour-public-offer__day-card-visual" aria-hidden>
            <SceneIcon className="organized-tour-public-offer__day-card-visual-icon" strokeWidth={2} size={26} />
          </div>
        </div>
      </article>
    );
  }

  if (item.type === "section") {
    const titleStr = String(item.title || "");
    const hideMisplacedExcludedTitle = /^לא\s*כולל/i.test(titleStr) || String(item.key) === "excluded";
    return (
      <article
        key={`sec-${index}-${item.key || item.title || index}`}
        className="organized-tour-public-offer__itinerary-prose-card"
      >
        {item.title && !hideMisplacedExcludedTitle ? (
          <h3 className="organized-tour-public-offer__itinerary-prose-heading">{item.title}</h3>
        ) : null}
        <RichParagraphBlocks
          paragraphs={item.paragraphs}
          paragraphClassName="organized-tour-public-offer__itinerary-prose-text"
          listClassName={listClassName}
        />
      </article>
    );
  }

  return (
    <article key={`prose-${index}`} className="organized-tour-public-offer__itinerary-prose-card">
      <RichParagraphBlocks
        paragraphs={item.paragraphs}
        paragraphClassName="organized-tour-public-offer__itinerary-prose-text"
        listClassName={listClassName}
      />
    </article>
  );
}

function SectionBlocks({ items, paragraphClassName, listClassName, emptyText = "—", showItemTitles = false }) {
  if (!items?.length) {
    return <p className={paragraphClassName}>{emptyText}</p>;
  }
  return (
    <>
      {items.map((item, idx) => (
        <div key={`${item.type}-${item.key || idx}-${idx}`} className="organized-tour-public-offer__tab-block">
          {showItemTitles && item.type === "section" && item.title ? (
            <h4 className="organized-tour-public-offer__tab-subhead">{item.title}</h4>
          ) : null}
          <RichParagraphBlocks
            paragraphs={item.paragraphs}
            paragraphClassName={paragraphClassName}
            listClassName={listClassName}
          />
        </div>
      ))}
    </>
  );
}

export default function OrganizedTourPublicOfferMainTabs({
  itineraryTextHe,
  parsed: parsedProp,
  additionalInfoHtml = "",
  linkedFlights = null,
  rooms = null,
  vm = null,
}) {
  const parsed = useMemo(() => {
    if (parsedProp) return parsedProp;
    return safeParseOrganizedTourItinerary(itineraryTextHe);
  }, [itineraryTextHe, parsedProp]);
  const model = useMemo(() => buildOrganizedTourOfferTabModel(parsed), [parsed]);

  const hasRenderableFlights = useMemo(
    () => extractOrganizedTourFlightLegs(linkedFlights).length > 0,
    [linkedFlights]
  );

  const hasRenderableRooms = useMemo(
    () => Array.isArray(rooms) && rooms.length > 0,
    [rooms]
  );

  const visibility = useMemo(() => {
    const hasItin = model.itineraryItems.length > 0;
    const showDescribeTab = model.describeItems.length > 0 || hasItin || hasRenderableFlights || hasRenderableRooms;
    return {
      describe: showDescribeTab,
      itinerary: hasItin,
      included: model.included.length > 0,
      excluded: model.excluded.length > 0,
      notes: true,
    };
  }, [model, hasRenderableFlights, hasRenderableRooms]);

  const visibleTabs = useMemo(
    () => TAB_META.filter((t) => visibility[t.id]),
    [visibility]
  );

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id || "describe");

  React.useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  if (!visibleTabs.length) {
    return (
      <div className="organized-tour-public-offer__section organized-tour-public-offer__main-card">
        <h2 className="organized-tour-public-offer__section-title organized-tour-public-offer__section-title--accent">
          תיאור הטיול
        </h2>
        <p className="organized-tour-public-offer__describe-text">—</p>
      </div>
    );
  }

  return (
    <div className="organized-tour-public-offer__section organized-tour-public-offer__main-card">
      <div className="organized-tour-public-offer__tabs" role="tablist" aria-label="תוכן ההצעה">
        <div className="organized-tour-public-offer__tabs-scroll">
          {TAB_META.map((t) => {
            if (!visibility[t.id]) return null;
            const Icon = t.Icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`organized-tour-public-offer__tab organized-tour-public-offer__tab--tone-${t.tone}${
                  isActive ? " organized-tour-public-offer__tab--active" : ""
                }`.trim()}
                onClick={() => setActiveTab(t.id)}
              >
                <span className="organized-tour-public-offer__tab-icon-ring" aria-hidden>
                  <Icon className="organized-tour-public-offer__tab-icon" strokeWidth={2} size={20} />
                </span>
                <span className="organized-tour-public-offer__tab-label">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="organized-tour-public-offer__tab-panels">
        {visibility.describe ? (
          <div
            role="tabpanel"
            hidden={activeTab !== "describe"}
            className={`organized-tour-public-offer__tab-panel${activeTab !== "describe" ? " organized-tour-public-offer__tab-panel--inactive" : ""}`.trim()}
            id="org-tour-tab-describe"
          >
            {model.describeItems.length > 0 ? (
              <>
                <h3 className="organized-tour-public-offer__panel-section-title">תיאור כללי</h3>
                <SectionBlocks
                  items={model.describeItems}
                  showItemTitles={false}
                  paragraphClassName="organized-tour-public-offer__describe-text"
                />
              </>
            ) : null}

            <OrganizedTourPublicOfferFlightsBlock
              linkedFlights={linkedFlights}
              titleAfterBlock={model.describeItems.length > 0}
            />

            <OrganizedTourPublicOfferRoomsBlock
              rooms={rooms}
              vm={vm}
              titleAfterBlock={model.describeItems.length > 0 || hasRenderableFlights}
            />

            {EMBED_ITINERARY_IN_DESCRIBE_TAB && model.itineraryItems.length > 0 ? (
              <section className="organized-tour-public-offer__embedded-itinerary">
                <h3
                  className={`organized-tour-public-offer__panel-section-title${
                    model.describeItems.length > 0 || hasRenderableFlights || hasRenderableRooms
                      ? " organized-tour-public-offer__panel-section-title--after-block"
                      : ""
                  }`.trim()}
                >
                  מסלול הטיול
                </h3>
                <div className="organized-tour-public-offer__itinerary-days">
                  {model.itineraryItems.map((item, idx) => (
                    <ItineraryTimelineItem key={`itin-embed-${item.type}-${idx}`} item={item} index={idx} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}

        {visibility.itinerary ? (
          <div
            role="tabpanel"
            hidden={activeTab !== "itinerary"}
            className={`organized-tour-public-offer__tab-panel${activeTab !== "itinerary" ? " organized-tour-public-offer__tab-panel--inactive" : ""}`.trim()}
            id="org-tour-tab-itinerary"
          >
            <h2 className="organized-tour-public-offer__section-title organized-tour-public-offer__section-title--accent">
              מסלול הטיול
            </h2>
            <OrganizedTourPublicOfferFlightsBlock linkedFlights={linkedFlights} titleAfterBlock={false} />
            <OrganizedTourPublicOfferRoomsBlock rooms={rooms} vm={vm} titleAfterBlock={hasRenderableFlights} />
            <div className="organized-tour-public-offer__itinerary-days">
              {model.itineraryItems.map((item, idx) => (
                <ItineraryTimelineItem key={`itin-${item.type}-${idx}`} item={item} index={idx} />
              ))}
            </div>
          </div>
        ) : null}

        {visibility.included ? (
          <div
            role="tabpanel"
            hidden={activeTab !== "included"}
            className={`organized-tour-public-offer__tab-panel organized-tour-public-offer__tab-panel--brochure-lists${
              activeTab !== "included" ? " organized-tour-public-offer__tab-panel--inactive" : ""
            }`.trim()}
            id="org-tour-tab-included"
          >
            <h2 className="organized-tour-public-offer__brochure-section-heading">כולל:</h2>
            <SectionBlocks
              items={model.included}
              paragraphClassName="organized-tour-public-offer__brochure-prose"
              listClassName="organized-tour-public-offer__brochure-bullet-list"
            />
          </div>
        ) : null}

        {visibility.excluded ? (
          <div
            role="tabpanel"
            hidden={activeTab !== "excluded"}
            className={`organized-tour-public-offer__tab-panel organized-tour-public-offer__tab-panel--brochure-lists${
              activeTab !== "excluded" ? " organized-tour-public-offer__tab-panel--inactive" : ""
            }`.trim()}
            id="org-tour-tab-excluded"
          >
            <h2 className="organized-tour-public-offer__brochure-section-heading">לא כולל:</h2>
            <SectionBlocks
              items={model.excluded}
              paragraphClassName="organized-tour-public-offer__brochure-prose"
              listClassName="organized-tour-public-offer__brochure-bullet-list"
            />
          </div>
        ) : null}

        {visibility.notes ? (
          <div
            role="tabpanel"
            hidden={activeTab !== "notes"}
            className={`organized-tour-public-offer__tab-panel${activeTab !== "notes" ? " organized-tour-public-offer__tab-panel--inactive" : ""}`.trim()}
            id="org-tour-tab-notes"
          >
            <h2 className="organized-tour-public-offer__section-title organized-tour-public-offer__section-title--accent">
              הערות חשובות
            </h2>
            {model.notes.length > 0 ? (
              <SectionBlocks items={model.notes} paragraphClassName="organized-tour-public-offer__describe-text" />
            ) : null}
            <OrganizedTourPublicOfferNotesLegal additionalInfoHtml={additionalInfoHtml} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
